import Big from 'big.js';
import * as buttons from 'smg/buttons';
import { AddCartItems } from 'smg/customer-api/v2/carts';
import * as models from 'smg/customer-api/models';
import { ShowError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import { IsObject } from 'smg/object';
import * as strings from 'smg/strings';
import * as template from 'smg/template';
import * as uploads from 'smg/uploads';
import { DollarFormat } from 'smg/util';
import { Relref } from '../hugo';
import * as pricecatalog from '../pricecatalog';
import * as stockitems from '../stockitems';

main();

function main() {
    stockitems.InitSeeMore();

    const $form = document.querySelector('#stock-item-form') as HTMLFormElement;
    const $fieldset = $form.querySelector('fieldset') as HTMLFieldSetElement;

    const $color = document.querySelector('#color') as HTMLSelectElement;
    const $size = document.querySelector('#size') as HTMLSelectElement;
    const $quantity = document.querySelector('#quantity-select') as HTMLSelectElement;
    const $quantityCustomParent = document.querySelector('#quantity-custom-parent') as HTMLElement;
    const $quantityCustom = $quantityCustomParent.querySelector('#quantity-custom') as HTMLInputElement;
    const $front3D = document.querySelector('#front-3d') as HTMLInputElement;
    const $back3D = document.querySelector('#back-3d') as HTMLInputElement;

    const $addToCart = document.querySelector('#add-to-cart') as HTMLButtonElement;
    const $priceStartsAt = document.querySelector('.price-starts-at') as HTMLElement;

    const $frontImgUploader = document.querySelector('#front-img-uploader') as HTMLElement;
    const $frontUploadsLabel = $frontImgUploader.querySelector('#front-img-uploads') as HTMLLabelElement;
    const $frontUploadsInput = $frontImgUploader.querySelector('input') as HTMLInputElement;
    const $frontThumbnailsParent = $frontImgUploader.querySelector('#front-thumbnails-parent') as HTMLElement;
    const $frontThumbnails = $frontThumbnailsParent.querySelector('#front-thumbnails') as HTMLElement;
    const $frontSpinner = $frontThumbnailsParent.querySelector('.spinner-border') as HTMLElement;
    const frontUploader: uploads.Uploader = new uploads.Uploader($frontUploadsLabel, {
        thumbnails: $frontThumbnails,
        start: () => $frontSpinner.classList.remove('d-none'),
        stop: () => $frontSpinner.classList.add('d-none'),
        thumbnailMaxHeight: '80px',
        onchange: () => {
            $frontThumbnailsParent.classList.remove('d-none');
        },
    });

    const $backImgUploader = document.querySelector('#back-img-uploader') as HTMLElement;
    const $backUploadsLabel = $backImgUploader.querySelector('#back-img-uploads') as HTMLLabelElement;
    const $backUploadsInput = $backImgUploader.querySelector('input') as HTMLInputElement;
    const $backThumbnailsParent = $backImgUploader.querySelector('#back-thumbnails-parent') as HTMLElement;
    const $backThumbnails = $backThumbnailsParent.querySelector('#back-thumbnails') as HTMLElement;
    const $backSpinner = $backThumbnailsParent.querySelector('.spinner-border') as HTMLElement;
    const backUploader: uploads.Uploader = new uploads.Uploader($backUploadsLabel, {
        thumbnails: $backThumbnails,
        start: () => $backSpinner.classList.remove('d-none'),
        stop: () => $backSpinner.classList.add('d-none'),
        thumbnailMaxHeight: '80px',
        onchange: () => {
            $backThumbnailsParent.classList.remove('d-none');
        },
    });

    interface childLineItem {
        child: string;
        option: string;
        quantity?: number;
    }

    interface getPriceFromOptionsOpts {
        lineItem?: string;
        lineItemType?: string;
        size?: string;
        quantity?: number;
        childLineItems?: childLineItem[];
    }

    document.querySelector('#quantity')?.parentElement?.remove();

    function getPriceFromOptions(opts: getPriceFromOptionsOpts): Big {
        const lineItemName = opts?.lineItem || defaultOpts.lineItem;
        const lineItemTypeName = opts?.lineItemType || defaultOpts.lineItemType;
        const sizeName = opts?.size || defaultOpts.size;
        const quantity = opts?.quantity || defaultOpts.quantity;
        const childLineItems = opts?.childLineItems || defaultOpts.childLineItems;

        if (!lineItemName || !lineItemTypeName || !sizeName || !quantity) {
            throw new Error('Missing required options for price');
        }

        // The standard Mold Fee child is not priced by its option here: a 3D coin is struck
        // from a two-sided mold whose fee the size's moldFeeSides carries (and waives past
        // the size's discovered quantity), so it rides the scenario's mold policy instead.
        const priced = Array.isArray(childLineItems) && IsObject(childLineItems[0]) ? childLineItems : [];
        const moldChild = priced.find(cli => cli.child === 'Mold Fee');
        const children = pricecatalog.ChildAmountsOf(lineItemName, priced.filter(cli => cli !== moldChild));
        return pricecatalog.UnitPrice({
            lineItem: lineItemName,
            type: lineItemTypeName,
            size: sizeName,
            quantity,
            perUnit: children.perUnit,
            fixed: children.fixed,
            mold: moldChild?.option.includes('Double') ? 'twoSided' : 'none',
        });
    }

    $front3D.value = 'low';
    $back3D.value = 'low';
    const children = childLineItemsFrom3DOptions();

    const defaultOpts: getPriceFromOptionsOpts = {
        lineItem: $priceStartsAt.getAttribute('data-line-item') ?? '',
        lineItemType: $priceStartsAt.getAttribute('data-line-item-type') ?? '',
        size: $priceStartsAt.getAttribute('data-size') ?? '',
        quantity: parseInt($priceStartsAt.getAttribute('data-quantity') || '0', 10),
        childLineItems: children,
    };

    $size.value = defaultOpts.size || $size.value;

    // Modified from stockitems initprices function
    function updatePrice(opts?: getPriceFromOptionsOpts) {
        const dollars = DollarFormat(getPriceFromOptions({
            lineItem: opts?.lineItem || defaultOpts.lineItem,
            lineItemType: opts?.lineItemType || $color.value || defaultOpts.lineItemType,
            size: opts?.size || $size.value || defaultOpts.size,
            quantity: opts?.quantity || parseInt($quantity.value, 10) || parseInt($quantityCustom.value, 10) || defaultOpts.quantity,
            childLineItems: opts?.childLineItems || childLineItemsFrom3DOptions() || defaultOpts.childLineItems,
        }));

        $priceStartsAt.innerText = dollars;

        if (!$priceStartsAt.classList.contains('price-starts-at')) {
            $priceStartsAt.innerText += ' each';
        }

        $priceStartsAt.classList.remove('placeholder-wave');
    }

    updatePrice();

    function updateSizeQtyPrices() {
        const selectedSizeValue = $size.value || defaultOpts.size || '';
        const selectedQuantityValue = $quantity.querySelector<HTMLOptionElement>('option:checked')?.value || defaultOpts.quantity?.toString() || '0';
        $size.innerHTML = '';
        $quantity.innerHTML = '<option value="">Custom</option>';
        const sizes = pricecatalog.Sizes(defaultOpts.lineItem ?? '', defaultOpts.lineItemType ?? '');
        const selectedSize = sizes.find(s => s.names.includes(selectedSizeValue.trim().toLowerCase()));
        if (!selectedSize) {
            throw new Error(`Size ${selectedSizeValue} not found`);
        }
        const selectedSizeLabel = selectedSize.name;
        const currentPrice = getPriceFromOptions({
            lineItem: defaultOpts.lineItem,
            lineItemType: $color.value,
            size: selectedSizeLabel,
            quantity: parseInt(selectedQuantityValue, 10),
            childLineItems: childLineItemsFrom3DOptions(),
        });
        for (const size of sizes) {
            const sizeLabel = size.name;
            const $option = document.createElement('option');
            $option.value = sizeLabel;
            $option.innerText = sizeLabel;
            const sizePrice = getPriceFromOptions({
                lineItem: defaultOpts.lineItem,
                lineItemType: $color.value,
                size: sizeLabel,
                quantity: parseInt(selectedQuantityValue, 10),
                childLineItems: childLineItemsFrom3DOptions(),
            });
            const priceDelta = sizePrice.minus(currentPrice);
            if (priceDelta.gt(0)) {
                $option.innerText += ` (+ ${DollarFormat(priceDelta)})`;
            } else if (priceDelta.lt(0)) {
                $option.innerText += ` (- ${DollarFormat(priceDelta.abs())})`;
            }
            $size.appendChild($option);
        }
        for (const price of selectedSize.prices) {
            const $option = document.createElement('option');
            $option.value = price.quantity.toString();
            const sizeQtyPrice = getPriceFromOptions({
                size: selectedSizeLabel,
                quantity: price.quantity,
            });
            $option.innerText = `${price.quantity}`;
            const priceDelta = sizeQtyPrice.minus(currentPrice);
            if (priceDelta.gt(0)) {
                $option.innerText += ` (+ ${DollarFormat(priceDelta)})`;
            } else if (priceDelta.lt(0)) {
                $option.innerText += ` (-${DollarFormat(priceDelta.abs())})`;
            }
            $quantity.appendChild($option);
        }

        $quantity.value = selectedQuantityValue;
        $size.value = selectedSizeValue;
    }

    $size.addEventListener('change', () => {
        updatePrice({ size: $size.value });
    });

    $color.addEventListener('change', () => {
        updatePrice({ lineItemType: $color.value });
    });

    $quantity.addEventListener('input', () => {
        if (!$quantity.value) {
            $quantityCustomParent.classList.remove('d-none');
            $quantityCustom.value = '50';
            $quantityCustom.required = true;
            $quantity.required = false;
            $quantity.setCustomValidity('');
            updatePrice({ quantity: 50 });
            return;
        } else {
            $quantityCustom.required = false;
            $quantity.required = true;
        }
        let quantity = parseInt($quantity.value, 10);
        if (quantity < 50) {
            quantity = 50;
        }
        updatePrice({ quantity });
    });

    $quantityCustom.addEventListener('input', () => {
        let quantity = parseInt($quantityCustom.value, 10);
        if (quantity < 50) {
            quantity = 50;
        } else if (quantity > 2000) {
            quantity = 2000;
        }
        updatePrice({ quantity });
    });

    updateSizeQtyPrices();

    document.querySelectorAll<HTMLOptionElement>('option:has(.price)').forEach($el => {
        let opts: getPriceFromOptionsOpts;
        switch ($el.getAttribute('data-option')) {
        case 'lineItemType':
            opts = {
                lineItemType: $el.value,
            };
            break;
        case 'size':
            opts = {
                size: $el.value,
            };
            break;
        default:
            opts = {};
            break;
        }
        const defaultPrice = getPriceFromOptions(defaultOpts);
        const optionPrice = getPriceFromOptions(opts);
        const priceDelta = optionPrice.minus(defaultPrice);
        const $price = $el.querySelector('.price') as HTMLElement;
        if (priceDelta.gt(0)) {
            $price.innerText = ` (+ ${DollarFormat(priceDelta)})`;
        } else if (priceDelta.lt(0)) {
            $price.innerText = ` (- ${DollarFormat(priceDelta.abs())})`;
        }
    });

    const moldFee3D = pricecatalog.OptionPrice(defaultOpts.lineItem ?? '', '3D Setup Fee', '3D Mold on One Side');
    const highReliefFee3D = pricecatalog.OptionPrice(defaultOpts.lineItem ?? '', '3D High-Relief Image', 'One Side');
    document.querySelectorAll<HTMLSpanElement>('.mold-price').forEach($option => {
        $option.innerText = `(${strings.TrimSuffix(DollarFormat(moldFee3D), '.00')})`;
    });
    document.querySelectorAll<HTMLSpanElement>('.mold-price-high-relief').forEach($option => {
        $option.innerText = `(${strings.TrimSuffix(DollarFormat(moldFee3D), '.00')} + ${strings.TrimSuffix(DollarFormat(highReliefFee3D), '.00')}/coin)`;
    });

    function childLineItemsFrom3DOptions() {
        const childLineItems: childLineItem[] = [];

        // Add standard mold fee with correct option based on number of sides
        if ($front3D.value !== 'none' && $back3D.value !== 'none') {
            childLineItems.push({
                child: 'Mold Fee',
                option: 'New Double-Sided Mold',
                quantity: 1,
            });
            childLineItems.push({
                child: '3D Setup Fee',
                option: '3D Mold on Both Sides',
                quantity: 2,
            });
            childLineItems.push({
                child: '3D Tooling Fee',
                option: 'Both Sides',
            });
        } else if ($front3D.value !== 'none' || $back3D.value !== 'none') {
            childLineItems.push({
                child: 'Mold Fee',
                option: 'New Double-Sided Mold',
                quantity: 1,
            });
            childLineItems.push({
                child: '3D Setup Fee',
                option: '3D Mold on One Side',
                quantity: 1,
            });
            childLineItems.push({
                child: '3D Tooling Fee',
                option: 'One Side',
            });
        }

        if ($front3D.value === 'high' && $back3D.value === 'high') {
            childLineItems.push({
                child: '3D High-Relief Image',
                option: 'Both Sides',
            });
        } else if ($front3D.value === 'high' || $back3D.value === 'high') {
            childLineItems.push({
                child: '3D High-Relief Image',
                option: 'One Side',
            });
        }

        return childLineItems;
    }

    const $lowImgs = Array.from(document.querySelectorAll<HTMLImageElement>('.gallery-image[data-attributes*="low"]'));
    const $highImgs = Array.from(document.querySelectorAll<HTMLImageElement>('.gallery-image[data-attributes*="high"]'));

    // Drop the first one, since the first one has both high and low.
    $lowImgs.shift();
    $highImgs.shift();

    // When the user makes a selection, show an image of that type. The image rotates through the gallery so the same one isn't shown twice in a row.
    $front3D.addEventListener('change', () => {
        if ($front3D.value === 'low') {
            const $next = $lowImgs?.shift();
            $next?.dispatchEvent(new MouseEvent('mouseover'));
            $lowImgs.push($next as HTMLImageElement);
        }
        if ($front3D.value === 'high') {
            const $next = $highImgs?.shift();
            $next?.dispatchEvent(new MouseEvent('mouseover'));
            $highImgs.push($next as HTMLImageElement);
        }
        updatePrice({ childLineItems: childLineItemsFrom3DOptions() });
        $frontUploadsInput.setCustomValidity('');
        $frontUploadsInput.dispatchEvent(new Event('clearinvalid'));
        $backUploadsInput.setCustomValidity('');
        $backUploadsInput.dispatchEvent(new Event('clearinvalid'));
    });

    $back3D.addEventListener('change', () => {
        if ($back3D.value === 'low') {
            const $next = $lowImgs?.shift();
            $next?.dispatchEvent(new MouseEvent('mouseover'));
            $lowImgs.push($next as HTMLImageElement);
        }
        if ($back3D.value === 'high') {
            const $next = $highImgs?.shift();
            $next?.dispatchEvent(new MouseEvent('mouseover'));
            $highImgs.push($next as HTMLImageElement);
        }
        updatePrice({ childLineItems: childLineItemsFrom3DOptions() });
        $frontUploadsInput.setCustomValidity('');
        $frontUploadsInput.dispatchEvent(new Event('clearinvalid'));
        $backUploadsInput.setCustomValidity('');
        $backUploadsInput.dispatchEvent(new Event('clearinvalid'));
    });

    $form.noValidate = true;
    $form.addEventListener('submit', async e => {
        e.preventDefault();

        if ($front3D.value === 'none' && $back3D.value === 'none') {
            $frontUploadsInput.setCustomValidity('Please choose at least one 3D option to continue');
            $backUploadsInput.setCustomValidity('Please choose at least one 3D option to continue');
        } else {
            if ($front3D.value !== 'none' && frontUploader.getUploads().length === 0) {
                $frontUploadsInput.setCustomValidity('Please upload an image');
            } else {
                $frontUploadsInput.setCustomValidity('');
                $frontUploadsInput.dispatchEvent(new Event('clearinvalid'));
            }

            if ($back3D.value !== 'none' && backUploader.getUploads().length === 0) {
                $backUploadsInput.setCustomValidity('Please upload an image');
            } else {
                $backUploadsInput.setCustomValidity('');
                $backUploadsInput.dispatchEvent(new Event('clearinvalid'));
            }
        }

        if (!ReportValidity($form)) {
            return;
        }

        $fieldset.disabled = true;
        buttons.Start($addToCart);

        // Awaited after disable — a PUT-failure throw would otherwise leave the form disabled.
        let frontUploadIDs: number[];
        let backUploadIDs: number[];
        try {
            frontUploadIDs = await frontUploader.getUploadIDs();
            backUploadIDs = await backUploader.getUploadIDs();
        } catch (err) {
            ShowError(err);
            $fieldset.disabled = false;
            buttons.Stop($addToCart);
            return;
        }
        AddCartItems({
            uploadID: frontUploadIDs[0] || backUploadIDs[0] || 0,
            quantity: parseInt($quantity.value, 10),
            cartItemUploads: [
                ...frontUploadIDs.slice(1).map(id => ({ uploadID: id, tags: ['proof', 'front'] })),
                ...backUploadIDs.map(id => ({ uploadID: id, tags: ['proof', 'back'] })),
            ],
            uploadLineItem: {
                name: '3D Challenge Coins (Prepayment)',
                lineItem: 'Challenge Coin',
                type: $color.value,
                size: $size.value,
                networkSKUID: models.NetworkSKUID.NETWORK_SK_US_3_D_CHALLENGE_COINS,
                flagIDs: [models.FlagID.FLAG_PRE_DESIGN],
                childLineItems: [
                    ...childLineItemsFrom3DOptions(),
                    {
                        child: 'plating',
                        option: 'gold',
                    },
                    {
                        child: 'edge',
                        option: 'standard',
                    },
                ],
            },
        }).then(() => {
            window.location.href = Relref('/cart');
        }).catch(err => {
            $fieldset.disabled = false;
            buttons.Stop($addToCart);
            ShowError(err);
        });
    });

    function initGallery() {
        const $stage = document.querySelector('#stage') as HTMLElement;

        const $overlayContainer = document.querySelector<HTMLElement>('#overlay-container');
        const $lrBottom = document.querySelector<HTMLTemplateElement>('#label-lr-bottom');
        const $hrBottom = document.querySelector<HTMLTemplateElement>('#label-hr-bottom');
        const $hrTop = document.querySelector<HTMLTemplateElement>('#label-hr-top');

        const $galleryContainer = document.querySelector<HTMLElement>('#gallery-image-container');
        if (!$galleryContainer || !$overlayContainer) {
            return;
        }
        const $galleryContainer2 = $galleryContainer;
        const $imgs = $galleryContainer.querySelectorAll<HTMLImageElement>('.gallery-image');

        interface reliefLabel {
            level: string;
            position: string;
        }

        interface galleryImageAttrs {
            relief?: reliefLabel[];
        }

        $imgs.forEach($img => {
            const $a = $img.closest('a') as HTMLAnchorElement;
            const $big = $a.nextElementSibling as HTMLAnchorElement;
            const attrJson = $img.getAttribute('data-attributes');
            const attrs = attrJson ? JSON.parse(attrJson) as galleryImageAttrs : {};
            $img.addEventListener('mouseover', () => {
                $overlayContainer.innerHTML = '';
                if (attrs?.relief?.length) {
                    for (const relief of attrs.relief) {
                        switch (relief.level) {
                        case 'high':
                            if (relief.position === 'bottom') {
                                $overlayContainer.appendChild(template.Clone($hrBottom ?? ''));
                            } else {
                                $overlayContainer.appendChild(template.Clone($hrTop ?? ''));
                            }
                            break;
                        case 'low':
                            if (relief.position === 'bottom') {
                                $overlayContainer.appendChild(template.Clone($lrBottom ?? ''));
                            }
                            break;
                        default:
                            continue;
                        }
                    }
                }
                $stage.innerHTML = '';
                $big.classList.remove('d-none');
                $stage.appendChild($big);
            });
        });

        $imgs[0].dispatchEvent(new MouseEvent('mouseover'));

        const $scrollUpBtn = document.querySelector('#scroll-up-btn') as HTMLElement;
        const $scrollDownBtn = document.querySelector('#scroll-down-btn') as HTMLElement;
        const $scrollLeftBtn = document.querySelector('#scroll-left-btn') as HTMLElement;
        const $scrollRightBtn = document.querySelector('#scroll-right-btn') as HTMLElement;

        const scrollBtns = [$scrollUpBtn, $scrollDownBtn, $scrollLeftBtn, $scrollRightBtn];

        for (const btn of scrollBtns) {
            btn.addEventListener('click', e => {
                e.preventDefault();

                switch (btn) {
                case $scrollUpBtn:
                    scrollGallery({ top: -100 }, 'smooth');
                    break;
                case $scrollDownBtn:
                    scrollGallery({ top: 100 }, 'smooth');
                    break;
                case $scrollLeftBtn:
                    scrollGallery({ left: -100 }, 'smooth');
                    break;
                case $scrollRightBtn:
                    scrollGallery({ left: 100 }, 'smooth');
                    break;
                }
            });
        }

        function scrollGallery(opts: ScrollToOptions, behavior?: ScrollBehavior) {
            $galleryContainer2.scrollBy({
                top: opts.top,
                left: opts.left,
                behavior,
            });
        }
    }

    initGallery();
}
